import React from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import FallbackIcon from './index.svg';
import theme from '@/theme';
import AppButton from '../app-button';
import { clearCacheAndReload } from '@/utils/clear-cache';
import { isStaleChunkError } from '@/utils/stale-chunk-reload';
import useOnlineStatus from '@/hooks/useOnlineStatus';

interface FallbackProps {
  /** The caught error, if any — used to tailor the recovery actions (e.g. stale-bundle vs. real crash). */
  error?: Error | null;
  /** Resets the nearest error boundary and re-renders its children. Absent for the app-level boundary. */
  onReset?: () => void;
}

const goToStart = () => {
  // `/` redirects to `/home` or `/auth` depending on auth state. Plain navigation, no Router context needed.
  window.location.assign('/');
};

const reloadApp = () => {
  window.location.reload();
};

const Fallback: React.FC<FallbackProps> = ({ error, onReset }) => {
  const { t } = useTranslation();
  const offline = !useOnlineStatus();
  const stale = !offline && !!error && isStaleChunkError(error);

  let title: string;
  if (offline) {
    title = 'offline.message.title';
  } else if (stale) {
    title = 'fallback.message.stale-title';
  } else {
    title = 'fallback.message.title';
  }

  return (
    <Box
      paddingY={7}
      paddingX={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      boxSizing="border-box"
      style={{ backgroundColor: theme.palette.common.white }}
      className="fallback"
    >
      <Box marginBottom={3}>
        <FallbackIcon />
      </Box>
      <Box marginBottom={1}>
        <Typography variant="subtitle1">{t(title)}</Typography>
      </Box>
      <Box marginBottom={5} marginX={8}>
        <Typography align="center" variant="body2">
          {t(offline ? 'offline.message.info' : 'fallback.message.info')}
        </Typography>
      </Box>

      <Box width="100%" maxWidth={320} display="flex" flexDirection="column" alignItems="center">
        {offline ? (
          // No reload / clear-cache here — both are useless without a connection. `ErrorBoundary`
          // also auto-recovers when the browser fires `online`.
          <>
            {onReset && (
              <Box marginBottom={2} width="100%">
                <AppButton fullWidth color="primary" variant="contained" onClick={onReset}>
                  {t('fallback.actions.try-again')}
                </AppButton>
              </Box>
            )}
            <Box width="100%">
              <AppButton
                fullWidth
                color="primary"
                variant={onReset ? 'outlined' : 'contained'}
                onClick={goToStart}
              >
                {t('fallback.actions.go-home')}
              </AppButton>
            </Box>
          </>
        ) : stale ? (
          <>
            <Box marginBottom={2} width="100%">
              <AppButton fullWidth color="primary" variant="contained" onClick={clearCacheAndReload}>
                {t('fallback.actions.update-reload')}
              </AppButton>
            </Box>
            <Box width="100%">
              <AppButton fullWidth color="primary" variant="outlined" onClick={goToStart}>
                {t('fallback.actions.go-home')}
              </AppButton>
            </Box>
          </>
        ) : (
          <>
            {onReset && (
              <Box marginBottom={2} width="100%">
                <AppButton fullWidth color="primary" variant="contained" onClick={onReset}>
                  {t('fallback.actions.try-again')}
                </AppButton>
              </Box>
            )}
            <Box marginBottom={2} width="100%">
              <AppButton
                fullWidth
                color="primary"
                variant={onReset ? 'outlined' : 'contained'}
                onClick={goToStart}
              >
                {t('fallback.actions.go-home')}
              </AppButton>
            </Box>
            <Box marginBottom={2} width="100%">
              <AppButton fullWidth color="primary" variant="text" onClick={reloadApp}>
                {t('fallback.actions.reload')}
              </AppButton>
            </Box>
            <AppButton color="primary" variant="text" size="small" onClick={clearCacheAndReload}>
              {t('fallback.actions.clear-cache')}
            </AppButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Fallback;
